<template>
  <div class="ele-body">
    <el-card shadow="never">
      <div class="term-toolbar">
        <div class="term-filters">
          <el-select
            v-model="currentFile"
            filterable
            clearable
            size="small"
            placeholder="日志文件"
            style="width: 220px"
            :loading="fileLoading"
            @change="onFileChange">
            <el-option
              v-for="item in files"
              :key="item.file"
              :value="item.file"
              :label="fileLabel(item)" />
          </el-select>
          <el-input
            v-model.trim="keyword"
            size="small"
            clearable
            placeholder="关键词"
            style="width: 200px; margin-left: 10px"
            @keyup.enter.native="reload"
            @clear="reload" />
          <el-switch
            v-model="autoRefresh"
            style="margin-left: 14px"
            active-text="自动刷新"
            @change="onAutoChange" />
        </div>
        <div class="term-meta">
          <span v-if="meta.mtime">{{ meta.mtime }}</span>
          <span v-if="meta.size != null">{{ formatSize(meta.size) }}</span>
          <span v-if="autoRefresh" class="term-live">LIVE</span>
          <el-button
            size="small"
            :type="prettyJson ? 'primary' : 'default'"
            class="ele-btn-icon"
            style="margin-left: 12px"
            @click="togglePretty">格式化</el-button>
          <el-button
            size="small"
            type="warning"
            class="ele-btn-icon"
            style="margin-left: 8px"
            @click="clearData"
            v-if="permission.includes('sys:upstreamapilog:clear')">清空</el-button>
        </div>
      </div>

      <div v-loading="contentLoading && !autoRefresh" class="term-wrap">
        <div ref="term" class="term-body" @scroll="onTermScroll">
          <div
            v-for="row in displayRows"
            :key="row.key"
            class="term-row">
            <span class="term-ln">{{ row.ln }}</span>
            <span class="term-code" v-html="row.html" />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function rawLogLines(content) {
  if (!content) {
    return [];
  }
  const lines = content.split(/\r\n|\n|\r/);
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] !== '') {
      out.push(lines[i]);
    }
  }
  return out;
}

function formatLogLines(content) {
  if (!content) {
    return [];
  }
  const rawLines = content.split(/\r\n|\n|\r/);
  const out = [];
  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    if (line === '') {
      continue;
    }
    const ts = line.match(/^(\[[^\]]+\])([\s\S]*)$/);
    if (ts) {
      if (out.length) {
        out.push('');
      }
      out.push(ts[1]);
      pushPrettyJson(out, ts[2]);
      continue;
    }
    if (out.length) {
      out.push('');
    }
    pushPrettyJson(out, line);
  }
  while (out.length && out[out.length - 1] === '') {
    out.pop();
  }
  return out;
}

function pushPrettyJson(out, raw) {
  const text = String(raw || '').trim();
  if (!text) {
    return;
  }
  try {
    const pretty = JSON.stringify(JSON.parse(text), null, 2);
    const rows = pretty.split('\n');
    for (let i = 0; i < rows.length; i++) {
      out.push(rows[i]);
    }
  } catch (e) {
    out.push(raw);
  }
}

function highlightLine(raw) {
  if (raw == null || raw === '') {
    return '&nbsp;';
  }
  const text = String(raw);
  if (/^\[[^\]]+]$/.test(text)) {
    return `<span class="tok-ts">${escapeHtml(text)}</span>`;
  }

  let html = escapeHtml(text);
  html = html.replace(
    /&quot;([^&]*?)&quot;(\s*:)/g,
    '<span class="tok-key">&quot;$1&quot;</span>$2'
  );
  html = html.replace(
    /&quot;([^&]*?)&quot;/g,
    '<span class="tok-str">&quot;$1&quot;</span>'
  );
  html = html.replace(
    /(^|[:,[{\s])(-?\d+(?:\.\d+)?)(?=$|[,\]}\s])/g,
    '$1<span class="tok-num">$2</span>'
  );
  return html || '&nbsp;';
}

export default {
  name: 'TransactionUpstreamApiLog',
  data() {
    return {
      keyword: '',
      files: [],
      fileLoading: false,
      contentLoading: false,
      currentFile: '',
      content: '',
      meta: { size: null, mtime: '' },
      lastContent: '',
      autoRefresh: true,
      prettyJson: false,
      timer: null,
      stickBottom: true,
      loadingReq: false
    };
  },
  computed: {
    ...mapGetters(['permission']),
    displayRows() {
      const lines = this.prettyJson
        ? formatLogLines(this.content)
        : rawLogLines(this.content);
      const pretty = this.prettyJson;
      const rows = new Array(lines.length);
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        rows[i] = {
          key: i,
          ln: i + 1,
          html: pretty ? highlightLine(line) : (escapeHtml(line) || '&nbsp;')
        };
      }
      return rows;
    }
  },
  mounted() {
    this.reloadFiles(true);
  },
  activated() {
    this.onAutoChange();
    if (this.currentFile) {
      this.loadContent(true);
    }
  },
  deactivated() {
    this.stopTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    fileLabel(item) {
      return `${item.file}（${this.formatSize(item.size)}）`;
    },
    formatSize(size) {
      const n = Number(size) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1048576).toFixed(1)} MB`;
    },
    reload() {
      this.lastContent = '';
      this.loadContent(false);
    },
    reloadFiles(selectFirst) {
      this.fileLoading = true;
      this.$http
        .get('/upstreamapierrorlog/index', { params: { _t: Date.now() } })
        .then((res) => {
          this.fileLoading = false;
          if (res.data.code !== 0) {
            this.files = [];
            this.$message.error(res.data.msg || '加载失败');
            return;
          }
          this.files = res.data.data || [];
          if (!this.files.length) {
            this.currentFile = '';
            this.content = '';
            this.meta = { size: null, mtime: '' };
            this.stopTimer();
            return;
          }
          const still = this.files.find((item) => item.file === this.currentFile);
          if (still) {
            this.currentFile = still.file;
          } else if (selectFirst || !this.currentFile) {
            this.currentFile = this.files[0].file;
          }
          this.loadContent(false);
          this.onAutoChange();
        })
        .catch((e) => {
          this.fileLoading = false;
          this.$message.error(e.message);
        });
    },
    onFileChange() {
      this.lastContent = '';
      this.stickBottom = true;
      this.loadContent(false);
      this.onAutoChange();
    },
    loadContent(silent) {
      if (!this.currentFile) {
        this.content = '';
        return;
      }
      if (this.loadingReq && silent) {
        return;
      }
      if (!silent) {
        this.contentLoading = true;
      }
      this.loadingReq = true;
      this.$http
        .get('/upstreamapierrorlog/read', {
          params: {
            file: this.currentFile,
            lines: 800,
            keyword: this.keyword,
            _t: Date.now()
          },
          headers: { 'Cache-Control': 'no-cache' }
        })
        .then((res) => {
          this.loadingReq = false;
          this.contentLoading = false;
          if (res.data.code !== 0) {
            if (!silent) {
              this.content = '';
              this.$message.error(res.data.msg || '读取失败');
            }
            return;
          }
          const data = res.data.data || {};
          const next = data.content;
          if (silent && next === this.lastContent) {
            return;
          }
          this.lastContent = next;
          this.meta = {
            size: Number(data.size) || 0,
            mtime: data.mtime
          };
          this.content = next;
          this.$nextTick(() => {
            if (this.stickBottom) {
              this.scrollToBottom();
            }
          });
        })
        .catch((e) => {
          this.loadingReq = false;
          this.contentLoading = false;
          if (!silent) {
            this.$message.error(e.message);
          }
        });
    },
    onTermScroll() {
      const el = this.$refs.term;
      if (!el) return;
      this.stickBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    },
    scrollToBottom() {
      const el = this.$refs.term;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      this.stickBottom = true;
    },
    onAutoChange() {
      this.stopTimer();
      if (!this.autoRefresh || !this.currentFile) {
        return;
      }
      this.timer = setInterval(() => {
        this.loadContent(true);
      }, 3000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    togglePretty() {
      this.prettyJson = !this.prettyJson;
      this.$nextTick(() => {
        if (this.stickBottom) {
          this.scrollToBottom();
        }
      });
    },
    clearData() {
      this.$confirm('确定清空全部上游报错日志吗？此操作不可恢复。', '清空', { type: 'warning' })
        .then(() => this.$http.post('/upstreamapierrorlog/clear'))
        .then((res) => {
          if (res.data.code !== 0) {
            this.$message.error(res.data.msg || '清空失败');
            return;
          }
          this.$message.success(res.data.msg || '已清空');
          this.currentFile = '';
          this.content = '';
          this.lastContent = '';
          this.meta = { size: null, mtime: '' };
          this.stopTimer();
          this.reloadFiles(true);
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.term-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}
.term-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.term-meta {
  color: #909399;
  font-size: 12px;
}
.term-meta span + span {
  margin-left: 12px;
}
.term-live {
  color: #67c23a !important;
  font-weight: 600;
}
.term-wrap {
  border: 1px solid #2b2b2b;
  border-radius: 2px;
  overflow: hidden;
  background: #1e1e1e;
}
.term-body {
  height: calc(100vh - 240px);
  min-height: 420px;
  overflow: auto;
  background: #1e1e1e;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 19px;
}
.term-row {
  display: flex;
  align-items: flex-start;
  min-height: 19px;
}
.term-ln {
  flex: 0 0 46px;
  padding: 0 12px 0 0;
  text-align: right;
  color: #858585;
  user-select: none;
  background: #1e1e1e;
  position: sticky;
  left: 0;
  line-height: 19px;
}
.term-code {
  flex: 1;
  padding: 0 12px 0 8px;
  color: #d4d4d4;
  white-space: pre-wrap;
  word-break: break-all;
  min-width: 0;
  line-height: 19px;
}
.term-code >>> .tok-ts {
  color: #7f848e;
}
.term-code >>> .tok-key {
  color: #c678dd;
}
.term-code >>> .tok-str {
  color: #e06c75;
}
.term-code >>> .tok-num {
  color: #61afef;
}
</style>
